import React from 'react';
import { Sun, CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

/**
 * Preview Vitamin D Education Page
 * Read-only educational content
 */
const PreviewVitaminDPage = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-amber-100 rounded-xl">
          <Sun className="h-8 w-8 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vitamin D</h1>
          <p className="text-slate-500">Understanding its role for CLL patients</p>
        </div>
      </div>

      {/* Key Points */}
      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-100 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Key Points</h2>
        <ul className="space-y-3">
          {[
            'Vitamin D deficiency is common in CLL patients',
            'Optimal levels may support immune function',
            'Safe sun exposure and supplementation can help',
            'Regular monitoring is important'
          ].map((point, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Content Sections */}
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Why Vitamin D Matters</h3>
          <p className="text-slate-600 leading-relaxed">
            Vitamin D plays a crucial role in immune system function. Research has shown that 
            many CLL patients have low vitamin D levels at diagnosis, and maintaining adequate 
            levels may support overall health outcomes. The exact mechanisms are still being 
            studied, but evidence suggests vitamin D helps regulate immune cell function.
          </p>
        </div>

        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Optimal Levels</h3>
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Deficient</p>
                <p className="font-semibold text-red-600">&lt;30 nmol/L</p>
              </div>
              <div>
                <p className="text-slate-500">Insufficient</p>
                <p className="font-semibold text-amber-600">30-50 nmol/L</p>
              </div>
              <div>
                <p className="text-slate-500">Sufficient</p>
                <p className="font-semibold text-green-600">50-125 nmol/L</p>
              </div>
              <div>
                <p className="text-slate-500">Optimal for CLL</p>
                <p className="font-semibold text-teal-600">75-100 nmol/L</p>
              </div>
            </div>
          </div>
          <p className="text-slate-600 text-sm">
            Many CLL specialists recommend targeting the higher end of the normal range. 
            Discuss your target level with your healthcare team.
          </p>
        </div>

        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">How to Improve Levels</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Sun className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Safe Sun Exposure</p>
                <p className="text-sm text-slate-600">10-15 minutes of midday sun on arms and legs, several times per week</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Supplementation</p>
                <p className="text-sm text-slate-600">Vitamin D3 (cholecalciferol) is preferred. Common doses range from 1000-5000 IU daily</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-amber-800">Important</p>
          <p className="text-sm text-amber-700">
            Always have your vitamin D levels tested before supplementing, and work with 
            your healthcare provider to determine the right dose for you.
          </p>
        </div>
      </div>

      {/* Save Button (disabled in preview) */}
      <div className="flex justify-center">
        <Button disabled className="opacity-50 cursor-not-allowed">
          Save to My Ideas
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-slate-200">
        <Link to="/preview/integrative-health" className="text-slate-600 hover:text-slate-900">
          ← Back to Integrative Health
        </Link>
        <Link to="/preview/nutrition" className="text-teal-600 hover:text-teal-700 flex items-center gap-1">
          Track Your Vitamin D <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

export default PreviewVitaminDPage;
